import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pp_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem('pp_token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('pp_token', res.data.token);
    setToken(res.data.token);
    // Fetch full user with role_info
    const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.token}` } });
    setUser(me.data);
    return res.data;
  };

  const register = async (name, email, password, role = 'sales') => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, role });
    localStorage.setItem('pp_token', res.data.token);
    setToken(res.data.token);
    const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.token}` } });
    setUser(me.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('pp_token');
    setToken(null);
    setUser(null);
  };

  const hasPermission = (perm) => {
    const perms = user?.role_info?.permissions || [];
    if (perms.includes('*')) return true;
    if (perms.includes(perm)) return true;
    const resource = perm.split('.')[0];
    return perms.includes(`${resource}.*`);
  };

  const canSeeNav = (navKey) => {
    const navItems = user?.role_info?.nav_items || ['dashboard'];
    return navItems.includes(navKey);
  };

  const isFieldHidden = (field) => {
    const hidden = user?.role_info?.hidden_fields || [];
    return hidden.includes(field);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, hasPermission, canSeeNav, isFieldHidden }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
